import { useQuery, useMutation } from '@tanstack/react-query';
import { useGT } from 'gt-react';
import type { ProductCatalogFilter } from '@erp/rpc';
import { orpc } from '@/api/rpc/client';
import { startRpcDownload } from '@/api/rpc/downloadUrl';
import { useToast } from '@/context/ToastContext';

export interface ProductCatalogListParams extends ProductCatalogFilter {
  limit?: number;
  offset?: number;
}

/** Paginated catalog rows (SKU + price lists) for the active org. */
export function useProductCatalogList(params: ProductCatalogListParams) {
  return useQuery(
    orpc.inventoryManagement.productCatalog.list.queryOptions({
      input: {
        limit: params.limit ?? 50,
        offset: params.offset ?? 0,
        search: params.search,
        family: params.family,
        group: params.group,
        model: params.model,
        customerId: params.customerId,
        priceListId: params.priceListId,
        noPriceList: params.noPriceList,
        status: params.status,
      },
    }),
  );
}

/** Distinct PRODVIEW (family, group, model) triples for the filter dropdowns. */
export function useProductCatalogClassifications() {
  return useQuery(
    orpc.inventoryManagement.productCatalog.classifications.queryOptions(),
  );
}

/**
 * Overall product + price counts for the filtered catalog (all pages). Keyed by
 * the filters only (no pagination), so flipping pages reuses the cached count.
 */
export function useProductCatalogTotals(
  filters: ProductCatalogFilter,
  options?: { enabled?: boolean },
) {
  return useQuery({
    ...orpc.inventoryManagement.productCatalog.totals.queryOptions({
      input: {
        search: filters.search,
        family: filters.family,
        group: filters.group,
        model: filters.model,
        customerId: filters.customerId,
        priceListId: filters.priceListId,
        noPriceList: filters.noPriceList,
        status: filters.status,
      },
    }),
    enabled: options?.enabled ?? true,
  });
}

// The export resolves to a streamed .xlsx rather than a JSON value, so it runs
// as a browser navigation to the procedure's RPC GET URL instead of through the
// typed client — see `startRpcDownload`. Taking the path from the query utils
// keeps it in step with the contract: renaming the procedure breaks this line.
const [EXPORT_EXCEL_PATH] =
  orpc.inventoryManagement.productCatalog.exportExcel.key();

/**
 * Export the whole filtered catalog as an .xlsx (ignores pagination, no row
 * cap). The file streams straight to disk with the browser's own progress UI.
 */
export function useExportProductCatalog() {
  const { showToast } = useToast();
  const gt = useGT();

  return useMutation({
    mutationFn: (filters: ProductCatalogFilter) =>
      startRpcDownload(EXPORT_EXCEL_PATH, filters),
    onSuccess: () => {
      showToast({
        message: gt(
          'Your export has started downloading. If the Excel file would be too large to open, it arrives as a zip of smaller workbooks.',
          {
            context:
              'toast when a product catalog Excel export begins downloading',
          },
        ),
        title: gt('Export started', {
          context: 'toast title when a product catalog export download begins',
        }),
        color: 'info',
        icon: 'info',
      });
    },
    onError: (error: Error) => {
      showToast({
        message: error.message || 'Failed to export the product catalog',
        title: 'Error',
        color: 'error',
        icon: 'error',
      });
    },
  });
}
